/* =========================================================================
   "Mis recursos" (/resources) — the tools and files a client can download.

   Visible to role "codenec-user-client", plus admins and testers for QA. The
   route is guarded in App.js; this page does not re-check, so it renders the
   same for everyone who reaches it.

   The catalog lives in config/resources.js. Three kinds of entry:
     file    → an anchor with `download`, pointing at codenec/public/resources/…
     link    → an anchor to somewhere else, opened in a new tab
     storage → a button: the file is in Cloud Storage, gated by storage.rules

   file and link are plain anchors on purpose. The starter-app zip elsewhere in
   the app builds bytes in JS and clicks a synthetic <a>, because that content
   does not exist until the student generates it. These files already exist, so
   letting the browser fetch them directly means no Blob in memory, a real
   progress bar on a slow phone connection, and a working right-click → Save as.

   storage cannot be an anchor: its URL does not exist until Firebase mints one
   for THIS signed-in person, and minting it is what enforces the rule. So it is
   a button that asks first and then hands the browser the link it got. The
   download itself is still the browser's - no Blob, same progress bar - which
   matters when the file is a ~279 MB installer.
   ========================================================================= */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { FaDownload, FaExternalLinkAlt, FaFolderOpen } from "react-icons/fa";
import { getDownloadURL, ref } from "firebase/storage";

import { storage } from "../../config/firebase-config";

import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import useReveal from "hooks/useReveal";

import { RESOURCES } from "../../config/resources";
// For .cn-grid, the blueprint texture every signed-in page's navy band shares.
import "../../styles/brand-auth.css";
import "./ResourcesPage.css";

/**
 * A Cloud Storage file: ask Firebase for a link, then let the browser fetch it.
 *
 * The rule is checked when getDownloadURL runs, so every failure here is a real
 * answer rather than a network hiccup - "you are signed out", "your account is
 * not a client account", or "nothing has been published yet". They are told
 * apart because the three have different fixes and only one of them is theirs.
 */
const StorageAction = ({ resource, t }) => {
    const [state, setState] = useState("idle");

    const start = async () => {
        if (state === "working") return;
        setState("working");
        try {
            const url = await getDownloadURL(ref(storage, resource.path));
            // The browser downloads it, not us: a Blob would hold ~279 MB in
            // memory and lose the progress bar. The link is short-lived, so it
            // is used immediately and never stored.
            window.location.assign(url);
            setState("idle");
        } catch (error) {
            setState(
                error?.code === "storage/object-not-found" ? "missing" : "denied"
            );
        }
    };

    return (
        <>
            <button
                type="button"
                className="resources-card__action mt-auto"
                onClick={start}
                disabled={state === "working"}
            >
                {state === "working" ? t("resources.preparing") : t("resources.download")}
                {resource.size && (
                    <span className="resources-card__size">{resource.size}</span>
                )}
            </button>
            {(state === "denied" || state === "missing") && (
                <p className="resources-card__error" role="alert">
                    {t(state === "missing" ? "resources.notPublished" : "resources.denied")}
                </p>
            )}
        </>
    );
};

const ResourceCard = ({ resource, index, t }) => {
    const isFile = resource.kind === "file";
    const isStorage = resource.kind === "storage";

    // rel="noreferrer" matters on the link cards: these point at third-party
    // tools, and target="_blank" without it hands the opened page a window
    // handle back into the app.
    const anchorProps = isFile
        ? { href: resource.href, download: resource.filename || true }
        : { href: resource.href, target: "_blank", rel: "noopener noreferrer" };

    // The reveal sits on the column, not the card: the card's own transition
    // (its hover lift) would otherwise replace the reveal's. The stagger
    // restarts every three cards, so a row lands left to right.
    return (
        <Col
            xs="12"
            md="6"
            lg="4"
            className="mb-4"
            data-reveal
            style={{ "--reveal-delay": `${120 + (index % 3) * 70}ms` }}
        >
            <Card className="resources-card h-100">
                <CardBody className="d-flex flex-column">
                    <div className="resources-card__icon" aria-hidden="true">
                        {isFile || isStorage ? <FaDownload /> : <FaExternalLinkAlt />}
                    </div>

                    <h5 className="resources-card__title">{t(resource.titleKey)}</h5>

                    {resource.descriptionKey && (
                        <p className="resources-card__description">
                            {t(resource.descriptionKey)}
                        </p>
                    )}

                    {/* mt-auto pins the action to the bottom so cards in a row
                        line up even when descriptions differ in length. */}
                    {isStorage ? (
                        <StorageAction resource={resource} t={t} />
                    ) : (
                        <a className="resources-card__action mt-auto" {...anchorProps}>
                            {isFile ? t("resources.download") : t("resources.open")}
                            {resource.size && (
                                <span className="resources-card__size">{resource.size}</span>
                            )}
                        </a>
                    )}
                </CardBody>
            </Card>
        </Col>
    );
};

const ResourcesPage = () => {
    const { t } = useTranslation("common");
    useReveal();

    return (
        <>
            <DemoNavbar />
            <main className="resources-page">
                {/* The navy band the Studio and account pages open with, so the
                    client area reads as one product. */}
                <section className="resources-band cn-grid">
                    <span className="cn-grain" aria-hidden="true" />
                    <Container className="container-lg">
                        <div className="resources-head" data-reveal>
                            <h1 className="resources-head__title">{t("resources.title")}</h1>
                            <p className="resources-head__subtitle">{t("resources.subtitle")}</p>
                        </div>
                    </Container>
                </section>

                <section className="resources-body">
                    <Container className="container-lg">
                        {RESOURCES.length === 0 ? (
                            // Real state, not a placeholder: the catalog ships empty
                            // and stays empty until resources are added to
                            // config/resources.js.
                            <div className="resources-empty" data-reveal style={{ "--reveal-delay": "120ms" }}>
                                <span className="resources-empty__tile" aria-hidden="true">
                                    <FaFolderOpen className="resources-empty__icon" aria-hidden="true" />
                                </span>
                                <p className="resources-empty__text">{t("resources.empty")}</p>
                            </div>
                        ) : (
                            <Row>
                                {RESOURCES.map((resource, index) => (
                                    <ResourceCard key={resource.id} resource={resource} index={index} t={t} />
                                ))}
                            </Row>
                        )}
                    </Container>
                </section>
            </main>
            <MainFooter />
        </>
    );
};

export default ResourcesPage;
